import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format, isToday } from 'date-fns';
import { X } from 'lucide-react';
import type { CalendarEvent } from '../../types/calendar';
import type { CalendarLinkSummary } from '../../services/calendarLinkSources';
import { dayIndexInSpan, type EventSpan } from '../../utils/calendarLayout';
import { EventBar } from './EventBar';
import { eventColor, type TagColorLookup } from './eventColors';

export interface DayEventsPopoverProps {
  day: Date;
  /** Everything that touches the day, in start order. */
  spans: EventSpan[];
  /** The element that opened the popover; it is positioned beneath it. */
  anchor: HTMLElement;
  tagColors: TagColorLookup;
  links: ReadonlyMap<string, CalendarLinkSummary>;
  onEventClick: (event: CalendarEvent) => void;
  onClose: () => void;
}

const WIDTH = 288;
const GAP = 6;
const MARGIN = 8;

/** What to show beside an event on a given day of its span. */
export function spanMetaForDay(span: EventSpan, day: Date): string {
  if (span.shape === 'timed') return `${format(span.start, 'HH:mm')} – ${format(span.displayEnd, 'HH:mm')}`;
  if (span.dayCount === 1) return 'All day';
  return `Day ${dayIndexInSpan(span, day)} of ${span.dayCount}`;
}

/**
 * The full list of a day's events, for cells that had to hide some. Rendered
 * through a portal so the card's rounded-corner clipping cannot cut it off.
 */
export const DayEventsPopover = ({ day, spans, anchor, tagColors, links, onEventClick, onClose }: DayEventsPopoverProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>(() => {
    const rect = anchor.getBoundingClientRect();
    return { top: rect.bottom + GAP, left: rect.left };
  });

  // Keep the panel inside the viewport once its real height is known.
  useLayoutEffect(() => {
    const rect = anchor.getBoundingClientRect();
    const height = panelRef.current?.offsetHeight ?? 0;
    let left = rect.left;
    if (left + WIDTH + MARGIN > window.innerWidth) left = Math.max(MARGIN, window.innerWidth - WIDTH - MARGIN);
    let top = rect.bottom + GAP;
    if (top + height + MARGIN > window.innerHeight && rect.top - height - GAP > MARGIN) top = rect.top - height - GAP;
    setPosition({ top, left });
  }, [anchor, spans.length]);

  useEffect(() => {
    const onPointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      // The anchor toggles the popover itself; closing here as well would reopen it.
      if (panelRef.current?.contains(target) || anchor.contains(target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // The panel is fixed to where the anchor was, so it closes when the page
    // moves underneath it; scrolling its own list is fine.
    const onScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`Events on ${format(day, 'EEEE, MMMM d')}`}
      className="fixed z-[70] glass-card shadow-popover p-2 animate-fade-in"
      style={{ top: position.top, left: position.left, width: WIDTH }}
    >
      <div className="flex items-center justify-between gap-2 px-1.5 pb-1.5 mb-1 border-b border-glass-200/10">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-white">{format(day, 'EEE d')}</span>
          <span className="text-xs text-glass-400">{format(day, 'MMMM yyyy')}</span>
          {isToday(day) && <span className="badge badge-accent">Today</span>}
        </div>
        <button type="button" onClick={onClose} className="btn-icon btn-icon-sm" title="Close" aria-label="Close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-0.5 max-h-72 overflow-y-auto">
        {spans.map(span => (
          <EventBar
            key={span.event.id}
            span={span}
            color={eventColor(span.event, tagColors)}
            linked={links.get(span.event.id)}
            size="md"
            showTime={false}
            meta={spanMetaForDay(span, day)}
            onClick={event => {
              onClose();
              onEventClick(event);
            }}
            className="w-full"
          />
        ))}
      </div>
    </div>,
    document.body,
  );
};
